import { useState, useEffect } from "react";
import { activate } from "../../../utils/keyboard-activate";
import type { CrmMediaResponse } from "../../../types/crm";
import { getMediaForProfile, deleteMedia } from "../../../services/crmMedia";
import "./PlayerMediaSection.css";

interface PlayerMediaSectionProps {
  profileId: string;
}

export const PlayerMediaSection = ({ profileId }: PlayerMediaSectionProps) => {
  const [media, setMedia] = useState<CrmMediaResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState<CrmMediaResponse | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchMedia = async () => {
    try {
      const data = await getMediaForProfile(profileId);
      setMedia(data);
    } catch {
      // silently fail — empty state is fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [profileId]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this media?")) return;
    setDeleting(id);
    try {
      await deleteMedia(id);
      setMedia((prev) => prev.filter((m) => m.id !== id));
      if (previewItem?.id === id) setPreviewItem(null);
    } catch {
      alert("Failed to delete media");
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="player-media-section">
      <div className="player-media-section__header">
        <h3 className="player-media-section__title">
          Media {media.length > 0 && <span className="player-media-section__count">{media.length}</span>}
        </h3>
      </div>

      {loading ? (
        <div className="player-media-section__loading">Loading media...</div>
      ) : media.length === 0 ? (
        <div className="player-media-section__empty">
          No media yet. Videos and images shared in this player's Slack channel will appear here.
        </div>
      ) : (
        <div className="player-media-section__grid">
          {media.map((item) => (
            <div
              key={item.id}
              className="player-media-section__item"
              {...activate(() => setPreviewItem(item))}
            >
              <div className="player-media-section__thumb-wrapper">
                {item.thumbnailUrl || item.mediaType === "Image" ? (
                  <img
                    src={item.thumbnailUrl || item.downloadUrl}
                    alt={item.originalFileName || "Media"}
                    className="player-media-section__thumb"
                  />
                ) : (
                  <div className="player-media-section__thumb-placeholder">
                    🎬
                  </div>
                )}
                {item.mediaType === "Video" && (
                  <span className="player-media-section__type-badge">VIDEO</span>
                )}
              </div>
              <div className="player-media-section__item-info">
                <span className="player-media-section__item-source">
                  {item.source} · {timeAgo(item.createdAt)}
                </span>
                <span className="player-media-section__item-size">
                  {formatFileSize(item.fileSizeBytes)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewItem && (
        <div className="player-media-section__lightbox" onClick={() => setPreviewItem(null)}>
          <div className="player-media-section__lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="player-media-section__lightbox-close"
              onClick={() => setPreviewItem(null)}
            >
              ×
            </button>
            {previewItem.mediaType === "Video" ? (
              <video
                src={previewItem.downloadUrl}
                controls
                autoPlay
                className="player-media-section__lightbox-media"
              />
            ) : (
              <img
                src={previewItem.downloadUrl}
                alt={previewItem.originalFileName || "Image"}
                className="player-media-section__lightbox-media"
              />
            )}
            <div className="player-media-section__lightbox-footer">
              <span>{previewItem.originalFileName || "Media"}</span>
              <span>{formatFileSize(previewItem.fileSizeBytes)}</span>
              <button
                className="player-media-section__delete-btn"
                onClick={() => handleDelete(previewItem.id)}
                disabled={deleting === previewItem.id}
              >
                {deleting === previewItem.id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
